import React, { useState } from 'react'
import { User, Plus, Edit, Trash2, Check, X, Settings, Briefcase } from 'lucide-react'
import { ContextProfile } from '../types'

interface ContextAnalysisProps {
  profiles: ContextProfile[]
  activeProfileId: string | null
  onAddProfile: () => void
  onUpdateProfile: (id: string, profile: Partial<ContextProfile>) => void
  onRemoveProfile: (id: string) => void
  onSetActiveProfile: (id: string | null) => void
}

export const ContextAnalysis: React.FC<ContextAnalysisProps> = ({
  profiles,
  activeProfileId,
  onAddProfile,
  onUpdateProfile,
  onRemoveProfile,
  onSetActiveProfile
}) => {
  const [editingProfileId, setEditingProfileId] = useState<string | null>(null)
  const [editingProfile, setEditingProfile] = useState<Partial<ContextProfile>>({})

  const handleEditProfile = (profile: ContextProfile) => {
    setEditingProfileId(profile.id)
    setEditingProfile(profile)
  }

  const handleSaveProfile = () => {
    if (editingProfileId) {
      onUpdateProfile(editingProfileId, editingProfile)
      setEditingProfileId(null)
      setEditingProfile({})
    }
  }

  const handleCancelEdit = () => {
    setEditingProfileId(null)
    setEditingProfile({})
  }

  const activeProfile = profiles.find(p => p.id === activeProfileId)

  return (
    <div className="space-y-6">
      <div className="bg-purple-50 border border-purple-200 rounded-lg p-4">
                    <h3 className="text-lg font-medium text-purple-900 mb-2">Context Analysis</h3>
        <p className="text-purple-800">
                      Profiles support more personalized analysis. You can provide more background, context and information about yourself and your business.
        </p>
      </div>

                  {/* Profile List */}
      <div className="space-y-4">
        <div className="flex items-center justify-between">
                      <h4 className="text-lg font-medium text-gray-900">Profiles</h4>
          <button
            onClick={onAddProfile}
            className="flex items-center space-x-2 px-3 py-2 bg-purple-600 text-white rounded-lg hover:bg-purple-700 transition-colors"
          >
            <Plus className="w-4 h-4" />
                          <span>Add Profile</span>
          </button>
        </div>

        {profiles.length === 0 ? (
          <div className="text-center py-8 border-2 border-dashed border-gray-300 rounded-lg">
            <User className="w-12 h-12 mx-auto mb-4 text-gray-400" />
            <h5 className="text-lg font-medium text-gray-900 mb-2">No Profiles Yet</h5>
            <p className="text-gray-600 mb-4">
              Create your first profile for more personalized analysis
            </p>
            <button
              onClick={onAddProfile}
              className="px-4 py-2 bg-purple-600 text-white rounded-lg hover:bg-purple-700 transition-colors"
            >
              Create Profile
            </button>
          </div>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            {profiles.map((profile) => (
              <div
                key={profile.id}
                className={`p-4 border-2 rounded-lg cursor-pointer transition-all ${
                  activeProfileId === profile.id
                    ? 'border-purple-500 bg-purple-50'
                    : 'border-gray-200 hover:border-gray-300'
                }`}
                onClick={() => onSetActiveProfile(profile.id)}
              >
                {editingProfileId === profile.id ? (
                  <div className="space-y-3">
                    <input
                      type="text"
                      value={editingProfile.name || profile.name}
                      onChange={(e) => setEditingProfile({ ...editingProfile, name: e.target.value })}
                      className="w-full p-2 border border-gray-300 rounded focus:ring-2 focus:ring-purple-500 focus:border-transparent"
                      placeholder="Profile Name"
                    />
                    <textarea
                      value={editingProfile.description || profile.description}
                      onChange={(e) => setEditingProfile({ ...editingProfile, description: e.target.value })}
                      className="w-full p-2 border border-gray-300 rounded focus:ring-2 focus:ring-purple-500 focus:border-transparent"
                      placeholder="Profile Description"
                      rows={2}
                    />
                    <div className="flex space-x-2">
                      <button
                        onClick={handleSaveProfile}
                        className="flex items-center space-x-1 px-2 py-1 bg-green-600 text-white rounded text-sm hover:bg-green-700"
                      >
                        <Check className="w-3 h-3" />
                        <span>Save</span>
                      </button>
                      <button
                        onClick={handleCancelEdit}
                        className="flex items-center space-x-1 px-2 py-1 bg-gray-600 text-white rounded text-sm hover:bg-gray-700"
                      >
                        <X className="w-3 h-3" />
                        <span>Cancel</span>
                      </button>
                    </div>
                  </div>
                ) : (
                  <div>
                    <div className="flex items-start justify-between mb-2">
                      <div className="flex items-center space-x-2">
                        <User className="w-5 h-5 text-purple-600" />
                        <h5 className="font-medium text-gray-900">{profile.name}</h5>
                        {activeProfileId === profile.id && (
                          <span className="px-2 py-1 bg-purple-100 text-purple-800 text-xs rounded-full">
                            Current
                          </span>
                        )}
                      </div>
                      <div className="flex space-x-1">
                        <button
                          onClick={(e) => {
                            e.stopPropagation()
                            handleEditProfile(profile)
                          }}
                          className="p-1 text-gray-400 hover:text-gray-600"
                        >
                          <Edit className="w-4 h-4" />
                        </button>
                        <button
                          onClick={(e) => {
                            e.stopPropagation()
                            onRemoveProfile(profile.id)
                          }}
                          className="p-1 text-gray-400 hover:text-red-600"
                        >
                          <Trash2 className="w-4 h-4" />
                        </button>
                      </div>
                    </div>
                    <p className="text-sm text-gray-600 mb-3">{profile.description}</p>
                    
                    {/* Personal Info Preview */}
                    {profile.personalInfo && Object.keys(profile.personalInfo).length > 0 && (
                      <div className="mb-3">
                        <h6 className="text-xs font-medium text-gray-700 mb-1">Personal Info</h6>
                        <div className="text-xs text-gray-600 space-y-1">
                          {profile.personalInfo.age && <div>Age: {profile.personalInfo.age}</div>}
                          {profile.personalInfo.occupation && <div>Occupation: {profile.personalInfo.occupation}</div>}
                          {profile.personalInfo.location && <div>Location: {profile.personalInfo.location}</div>}
                        </div>
                      </div>
                    )}
                    
                    {/* Preferences Preview */}
                    <div>
                                              <h6 className="text-xs font-medium text-gray-700 mb-1">Preferences</h6>
                      <div className="text-xs text-gray-600 space-y-1">
                        <div>Risk Tolerance: {profile.preferences.riskTolerance}</div>
                        <div>Time Horizon: {profile.preferences.timeHorizon}</div>
                      </div>
                    </div>
                  </div>
                )}
              </div>
            ))}
          </div>
        )}
      </div>

      {/* Current Active Profile Details */}
      {activeProfile && (
        <div className="bg-white border border-gray-200 rounded-lg p-6">
          <div className="flex items-center space-x-2 mb-4">
            <Settings className="w-5 h-5 text-purple-600" />
            <h4 className="text-lg font-medium text-gray-900">Current Profile Details</h4>
          </div>
          
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            {/* Personal Information */}
            <div>
                              <h5 className="font-medium text-gray-900 mb-3">Personal Information</h5>
              <div className="space-y-2 text-sm">
                <div>
                  <span className="text-gray-600">Name:</span>
                  <span className="ml-2 text-gray-900">{activeProfile.name}</span>
                </div>
                {activeProfile.personalInfo?.age && (
                  <div>
                    <span className="text-gray-600">Age:</span>
                    <span className="ml-2 text-gray-900">{activeProfile.personalInfo.age}</span>
                  </div>
                )}
                {activeProfile.personalInfo?.occupation && (
                  <div>
                    <span className="text-gray-600">Occupation:</span>
                    <span className="ml-2 text-gray-900">{activeProfile.personalInfo.occupation}</span>
                  </div>
                )}
                {activeProfile.personalInfo?.location && (
                  <div>
                    <span className="text-gray-600">Location:</span>
                    <span className="ml-2 text-gray-900">{activeProfile.personalInfo.location}</span>
                  </div>
                )}
              </div>
            </div>

            {/* Preferences */}
            <div>
                              <h5 className="font-medium text-gray-900 mb-3">Preferences</h5>
              <div className="space-y-2 text-sm">
                <div>
                  <span className="text-gray-600">Risk Tolerance:</span>
                  <span className="ml-2 text-gray-900">{activeProfile.preferences.riskTolerance}</span>
                </div>
                <div>
                  <span className="text-gray-600">Time Horizon:</span>
                  <span className="ml-2 text-gray-900">{activeProfile.preferences.timeHorizon}</span>
                </div>
                {activeProfile.preferences.priorities.length > 0 && (
                  <div>
                    <span className="text-gray-600">Priorities:</span>
                    <div className="ml-2 mt-1">
                      {activeProfile.preferences.priorities.map((priority, index) => (
                        <span key={index} className="inline-block px-2 py-1 bg-purple-100 text-purple-800 text-xs rounded mr-1 mb-1">
                          {priority}
                        </span>
                      ))}
                    </div>
                  </div>
                )}
              </div>
            </div>
          </div>

                      {/* Business Background */}
          {activeProfile.businessContext && Object.keys(activeProfile.businessContext).length > 0 && (
            <div className="mt-6 pt-6 border-t border-gray-200">
              <div className="flex items-center space-x-2 mb-3">
                <Briefcase className="w-5 h-5 text-purple-600" />
                <h5 className="font-medium text-gray-900">Business Background</h5>
              </div>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4 text-sm">
                {activeProfile.businessContext.industry && (
                  <div>
                    <span className="text-gray-600">Industry:</span>
                    <span className="ml-2 text-gray-900">{activeProfile.businessContext.industry}</span>
                  </div>
                )}
                {activeProfile.businessContext.companySize && (
                  <div>
                    <span className="text-gray-600">Company Size:</span>
                    <span className="ml-2 text-gray-900">{activeProfile.businessContext.companySize}</span>
                  </div>
                )}
                {activeProfile.businessContext.role && (
                  <div>
                    <span className="text-gray-600">Role:</span>
                    <span className="ml-2 text-gray-900">{activeProfile.businessContext.role}</span>
                  </div>
                )}
              </div>
            </div>
          )}
        </div>
      )}

      <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
                    <h4 className="font-medium text-blue-900 mb-2">Usage Guide</h4>
            <ul className="text-sm text-blue-800 space-y-1">
              <li>• Create multiple profiles to adapt to different decision scenarios</li>
              <li>• Each profile contains personal information, preferences, and business background</li>
              <li>• Select an active profile to get more personalized analysis recommendations</li>
              <li>• Profile information will be used to optimize AI analysis results</li>
            </ul>
      </div>
    </div>
  )
} 